import React, { useMemo } from 'react';

import { Box, Button, Grid, Group, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';

import { useGetLeadVendorsListQuery } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { parseImplantDate } from '@/utils/implantDate';
import { isArchivedLead, isInactiveLead } from '@/utils/leadChambers';
import { LeadChamberSelect } from '@/components/LeadChamberSelect';

import { LabelAndValue } from '@/types/custom';
import { Lead } from '@/types/matchDevice';
import Dropdown from '@/ui/Dropdown';
import IconButton from '@/ui/IconButton';
import TextInputWrapper from '@/ui/TextInputWrapper';

import RecallBanner from '../../RecallBanner';

import styles from './LeadsEditModal.module.scss';

interface LeadsEditModalProps {
  leadsData?: Lead[];
  onLeadFieldChange: (leadId: string, field: keyof Lead, value: any) => void;
  onAddLead: (newLead: Lead) => void;
  onDeleteLead: (leadId: string) => void;
  leadStatusErrors: Record<string, string>;
  onLeadStatusError: (leadId: string, error?: string) => void;
  deviceId?: string;
}

const LEAD_LOCATION_OPTIONS: LabelAndValue[] = [
  { label: '-', value: '' },
  { label: 'Apex', value: 'Apex' },
  { label: 'Epicardal', value: 'Epicardal' },
  { label: 'Inflow', value: 'Inflow' },
  { label: 'Lateral Wall', value: 'Lateral Wall' },
  { label: 'Outflow', value: 'Outflow' },
  { label: 'Septum', value: 'Septum' },
  { label: 'Appendage', value: 'Appendage' },
  { label: 'LBB', value: 'LBB' }
];

const LEAD_CAPPED_OPTIONS: LabelAndValue[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const LEAD_STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' }
];

const valueExists = (value: string | null | undefined): boolean => {
  return value !== null && value !== undefined && value !== '' && value !== '-';
};

const generateTempId = (): string => {
  return `tempid-${Math.random().toString(36).slice(2)}`;
};

const LeadsEditModal: React.FC<LeadsEditModalProps> = ({
  leadsData = [],
  onLeadFieldChange,
  onAddLead,
  onDeleteLead,
  leadStatusErrors,
  onLeadStatusError,
  deviceId
}) => {
  const { data: leadVendorsList } = useGetLeadVendorsListQuery();

  // Display leads without grouping by chamber
  const displayLeads = useMemo(() => {
    return leadsData.filter((lead) => !isArchivedLead(lead.leadStatus));
  }, [leadsData]);

  const leadOptions: LabelAndValue[] = useMemo(() => {
    if (!leadVendorsList) return [];
    return leadVendorsList.map((vendor) => ({
      value: vendor.leadVendorId,
      label: vendor.leadVendorName ?? ''
    }));
  }, [leadVendorsList]);

  const handleFieldChange = (fieldName: keyof Lead, value: any, leadId: string) => {
    onLeadFieldChange(leadId, fieldName, value);
  };

  const handleAddLead = () => {
    const newLead: Lead = {
      id: generateTempId(),
      leadChamber: '',
      leadLocation: undefined,
      implantDate: undefined,
      serialNumber: undefined,
      leadCapped: undefined,
      leadStatus: undefined,
      deviceId: deviceId || '',
      leadModel: undefined,
      leadManufacturer: { leadVendorId: undefined, leadVendorName: undefined }
    };
    onAddLead(newLead);
  };

  const handleImplantDateChange = (date: Date | null, leadId: string) => {
    if (date === undefined) return;
    const dateString = date === null ? '' : dayjs(date).format('MM/DD/YYYY');
    handleFieldChange('implantDate', dateString, leadId);
  };

  const handleLeadStatusChange = (option: LabelAndValue | null, leadId: string) => {
    onLeadStatusError(leadId, undefined);
    onLeadFieldChange(leadId, 'leadStatus', option?.value || null);
  };

  const handleLeadCappedChange = (option: LabelAndValue | null, leadId: string) => {
    const value = option?.value === 'true' ? true : option?.value === 'false' ? false : undefined;
    onLeadFieldChange(leadId, 'leadCapped', value);
  };

  const getLeadCappedValue = (leadCapped?: boolean) => {
    if (leadCapped === null || leadCapped === undefined) return { label: '', value: '' };
    return leadCapped ? { label: 'Yes', value: 'true' } : { label: 'No', value: 'false' };
  };

  const getManufacturerValue = (lead: Lead) => {
    return (
      leadOptions.find((opt) => Number(opt.value) === lead.leadManufacturer?.leadVendorId) || {
        label: '',
        value: ''
      }
    );
  };

  const getLeadLocationValue = (leadLocation?: string | null) => {
    return (
      LEAD_LOCATION_OPTIONS.find((opt) => opt.value === leadLocation) || LEAD_LOCATION_OPTIONS[0]
    );
  };

  const renderLeadFields = (lead: Lead) => {
    const isInactive = isInactiveLead(lead.leadStatus);
    return (
      <>
        <Grid align="center" className={styles.leadFieldsGrid}>
          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <LeadChamberSelect
              value={lead.leadChamber}
              onChange={(value) => handleFieldChange('leadChamber', value, lead.id ?? '')}
              disabled={isInactive}
              label="Lead Chamber"
              placeholder="Select chamber"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Manufacturer"
              options={leadOptions}
              value={getManufacturerValue(lead)}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) =>
                handleFieldChange(
                  'leadManufacturer',
                  option
                    ? {
                        leadVendorId: option.value,
                        leadVendorName: option.label
                      }
                    : null,
                  lead.id ?? ''
                )
              }
              placeholder="Select Manufacturer"
              isDisabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <TextInputWrapper
              label="Model"
              value={valueExists(lead.leadModel) ? lead.leadModel : ''}
              onChange={(e) => handleFieldChange('leadModel', e.target.value, lead.id ?? '')}
              disabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <DateInput
              label="Implant Date"
              placeholder="MM/DD/YYYY"
              valueFormat="MM/DD/YYYY"
              dateParser={parseImplantDate}
              disabled={isInactive}
              clearable
              popoverProps={{ withinPortal: true }}
              value={parseImplantDate(lead.implantDate)}
              onChange={(date) => handleImplantDateChange(date, lead.id ?? '')}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <TextInputWrapper
              label="Serial Number"
              value={valueExists(lead.serialNumber) ? lead.serialNumber : ''}
              onChange={(e) => handleFieldChange('serialNumber', e.target.value, lead.id ?? '')}
              disabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Lead Location"
              options={LEAD_LOCATION_OPTIONS}
              value={getLeadLocationValue(lead.leadLocation)}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) => handleFieldChange('leadLocation', option?.value, lead.id ?? '')}
              placeholder="Select Lead Location"
              isDisabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Lead Capped"
              options={LEAD_CAPPED_OPTIONS}
              value={getLeadCappedValue(lead.leadCapped)}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) => handleLeadCappedChange(option, lead.id ?? '')}
              placeholder="Lead Capped"
              isDisabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Lead Status"
              options={LEAD_STATUS_OPTIONS}
              value={{
                label: lead.leadStatus ?? '',
                value: lead.leadStatus ?? ''
              }}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) => handleLeadStatusChange(option, lead.id ?? '')}
              placeholder="Lead Status"
            />
            {leadStatusErrors[lead.id ?? ''] && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {leadStatusErrors[lead.id ?? '']}
              </div>
            )}
          </Grid.Col>
        </Grid>
        {lead?.recall && (
          <RecallBanner deviceRecallData={{ ...lead.recall, location: lead.leadChamber } as any} />
        )}
      </>
    );
  };

  const handleDeleteLead = (lead: Lead) => {
    onDeleteLead(lead.id ?? '');
  };

  return (
    <Box className={styles.leadSectionGroup}>
      <Group
        justify="space-between"
        align="center"
        mb={8}
        wrap="nowrap"
        className={styles.leadSectionHeader}>
        <Text fw={600} size="sm">
          Leads
        </Text>
        <Button
          style={{ marginRight: '16px' }}
          size="compact-sm"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() => handleAddLead()}>
          Add Lead
        </Button>
      </Group>

      {displayLeads.length === 0 ? (
        <Text size="sm" c="dimmed" mb="sm">
          No leads. Use Add Lead to record one.
        </Text>
      ) : (
        <Grid gutter="xs" style={{ paddingRight: '16px', paddingLeft: '16px' }}>
          {displayLeads.map((lead) => (
            <Grid.Col span={12} key={lead.id}>
              <Box
                style={{
                  border: '1px solid #e2e6ec',
                  borderRadius: 8,
                  padding: 12,
                  background: '#fff'
                }}>
                <Group justify="space-between" mb={8} wrap="nowrap">
                  <Text size="xs" fw={600} c="dimmed">
                    Lead {lead.leadChamber ? `· ${lead.leadChamber}` : ''}
                  </Text>
                  <IconButton
                    icon="trash"
                    label="Delete"
                    className={styles.deleteButton}
                    onClick={() => handleDeleteLead(lead)}
                  />
                </Group>
                {renderLeadFields(lead)}
              </Box>
            </Grid.Col>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default LeadsEditModal;
